/**
 * Error Boundary - Catches JavaScript errors and shows fallback UI
 * Prevents white screen crashes in production
 *
 * CRASH LOGGING: Saves errors to localStorage for post-mortem analysis
 */

import React from 'react';
import { debug } from '../lib/debug.js';

// Key for storing crash data in localStorage
const CRASH_LOG_KEY = 'airline_ops_crash_log';
const MAX_CRASH_LOGS = 5;

// Save error to localStorage (survives page reload)
function saveCrashLog(error, errorInfo, source = 'ErrorBoundary') {
  try {
    const logs = JSON.parse(localStorage.getItem(CRASH_LOG_KEY) || '[]');
    const crashEntry = {
      timestamp: new Date().toISOString(),
      source,
      error: error?.toString() || 'Unknown error',
      message: error?.message || '',
      stack: error?.stack || '',
      componentStack: errorInfo?.componentStack || '',
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
    logs.unshift(crashEntry);
    // Keep only last N logs
    localStorage.setItem(CRASH_LOG_KEY, JSON.stringify(logs.slice(0, MAX_CRASH_LOGS)));
    debug('[CRASH LOG] Saved to localStorage:', crashEntry);
  } catch (e) {
    console.error('[CRASH LOG] Failed to save:', e);
  }
}

// Get saved crash logs
function getCrashLogs() {
  try {
    return JSON.parse(localStorage.getItem(CRASH_LOG_KEY) || '[]');
  } catch {
    return [];
  }
}

// Clear crash logs
function clearCrashLogs() {
  try {
    localStorage.removeItem(CRASH_LOG_KEY);
  } catch (e) {
    console.error('[CRASH LOG] Failed to clear:', e);
  }
}

// Global error handlers (catches errors outside React)
if (typeof window !== 'undefined') {
  window.onerror = function(message, source, lineno, colno, error) {
    saveCrashLog(error || new Error(message), { componentStack: `at ${source}:${lineno}:${colno}` }, 'window.onerror');
    return false; // Let default handler also run
  };

  window.onunhandledrejection = function(event) {
    saveCrashLog(event.reason, { componentStack: 'Unhandled Promise rejection' }, 'unhandledrejection');
  };

  // Periodic memory/state snapshot for debugging
  let lastStateSnapshot = null;
  window.__saveStateSnapshot = function(state) {
    lastStateSnapshot = {
      timestamp: new Date().toISOString(),
      flightCount: state?.flights?.length || 0,
      activeFlights: state?.flights?.filter(f => f.status !== 'completed')?.length || 0,
      dayNumber: state?.dayNumber || 0,
      gameSpeed: state?.gameSpeed || 1,
    };
  };

  // Save state on crash
  const originalOnerror = window.onerror;
  window.onerror = function(...args) {
    if (lastStateSnapshot) {
      try {
        const logs = JSON.parse(localStorage.getItem(CRASH_LOG_KEY) || '[]');
        if (logs[0]) {
          logs[0].gameState = lastStateSnapshot;
          localStorage.setItem(CRASH_LOG_KEY, JSON.stringify(logs));
        }
      } catch (e) {}
    }
    return originalOnerror?.(...args);
  };
}

// Export for external use
export { saveCrashLog, getCrashLogs, clearCrashLogs };

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, previousCrash: null };

    // Check for previous crash on mount — show briefly then auto-clear
    const logs = getCrashLogs();
    if (logs.length > 0) {
      this.state.previousCrash = logs[0];
      // Auto-clear after 8 seconds — if app loaded fine, the crash was from a previous build
      setTimeout(() => {
        clearCrashLogs();
        this.setState({ previousCrash: null });
      }, 8000);
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);

    // PERSIST ERROR TO LOCALSTORAGE
    saveCrashLog(error, errorInfo, 'ErrorBoundary');

    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    // Clear ONLY non-save data to prevent save loss
    try {
      // Keep saves, only clear potentially corrupted runtime data
      const saveKeys = ['airlineOpsWorldState', 'playerAirline', 'gameMode'];
      const savedData = {};

      // Backup save data
      saveKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) savedData[key] = value;
      });

      localStorage.clear();

      // Restore save data
      Object.entries(savedData).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });

      debug('[ErrorBoundary] Cleared localStorage (preserved saves)');
    } catch (e) {
      console.warn('[ErrorBoundary] Failed to clear localStorage:', e);
    }
    window.location.reload();
  };

  handleClearCrashLogs = () => {
    clearCrashLogs();
    this.setState({ previousCrash: null });
  };

  handleDismissPreviousCrash = () => {
    this.setState({ previousCrash: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#0a0a0a',
          color: '#e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          <div style={{
            maxWidth: '600px',
            textAlign: 'center',
          }}>
            <h1 style={{
              color: '#ef4444',
              fontSize: '24px',
              marginBottom: '16px',
            }}>
              Something went wrong
            </h1>

            <p style={{
              color: '#666',
              marginBottom: '24px',
            }}>
              The game encountered an unexpected error. This has been logged.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '32px' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #60ffd0',
                  color: '#60ffd0',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                }}
              >
                Reload Game
              </button>

              <button
                onClick={this.handleClearAndReload}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #666',
                  color: '#999',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                }}
              >
                Clear Data & Reload
              </button>
            </div>

            {/* Show error details in development */}
            {this.state.error && (
              <details style={{
                textAlign: 'left',
                backgroundColor: '#111',
                padding: '16px',
                borderRadius: '4px',
                border: '1px solid #333',
              }}>
                <summary style={{ cursor: 'pointer', color: '#666', marginBottom: '8px' }}>
                  Technical Details (saved to crash log)
                </summary>
                <pre style={{
                  color: '#ef4444',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0,
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            {/* Show all crash logs */}
            <details style={{
              textAlign: 'left',
              backgroundColor: '#111',
              padding: '16px',
              borderRadius: '4px',
              border: '1px solid #333',
              marginTop: '16px',
            }}>
              <summary style={{ cursor: 'pointer', color: '#666', marginBottom: '8px' }}>
                Crash History ({getCrashLogs().length} logs)
              </summary>
              <pre style={{
                color: '#fbbf24',
                fontSize: '11px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                maxHeight: '300px',
                overflow: 'auto',
              }}>
                {JSON.stringify(getCrashLogs(), null, 2)}
              </pre>
              <button
                onClick={this.handleClearCrashLogs}
                style={{
                  marginTop: '8px',
                  padding: '6px 12px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #666',
                  color: '#999',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '11px',
                }}
              >
                Clear Crash Logs
              </button>
            </details>
          </div>
        </div>
      );
    }

    // Show previous crash notification (dismissable)
    const prevCrash = this.state.previousCrash;
    if (prevCrash) {
      return (
        <>
          {this.props.children}
          <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: '#1a1a1a',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '16px',
            maxWidth: '400px',
            zIndex: 9999,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '12px',
          }}>
            <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '8px' }}>
              Previous Crash Detected
            </div>
            <div style={{ color: '#999', marginBottom: '8px' }}>
              {prevCrash.timestamp}
            </div>
            <div style={{ color: '#fbbf24', marginBottom: '8px' }}>
              {prevCrash.message || prevCrash.error}
            </div>
            {prevCrash.gameState && (
              <div style={{ color: '#666', fontSize: '10px', marginBottom: '8px' }}>
                Flights: {prevCrash.gameState.flightCount} | Day: {prevCrash.gameState.dayNumber} | Speed: {prevCrash.gameState.gameSpeed}x
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={this.handleDismissPreviousCrash}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#333',
                  border: '1px solid #666',
                  color: '#ccc',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '11px',
                }}
              >
                Dismiss
              </button>
              <button
                onClick={() => {
                  debug('[CRASH LOG]', getCrashLogs());
                  alert(JSON.stringify(getCrashLogs(), null, 2));
                }}
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#333',
                  border: '1px solid #666',
                  color: '#ccc',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '11px',
                }}
              >
                View All Logs
              </button>
            </div>
          </div>
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
